import React from "react";
import api from "@/lib/api";
import { X, ShieldCheck, CheckCircle, WarningCircle, Spinner } from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";

const FIELDS = [
  ["procurement_bu", "Procurement BU", "e.g. Vision Operations"],
  ["business_unit", "Business Unit", "e.g. Vision Operations"],
  ["legal_employer", "Legal Employer", "e.g. Vision Corporation"],
  ["ledger_name", "Ledger Name", "e.g. Vision Operations (USA)"],
  ["default_currency", "Default Currency", "USD"],
  ["default_country", "Default Country", "US"],
];

export default function ProfileSetupModal() {
  const { setupOpen, setSetupOpen, profile, onProfileSaved, setPendingAgent } = useErp();
  const [form, setForm] = React.useState({});
  const [pwd, setPwd] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);

  React.useEffect(() => {
    if (setupOpen) {
      setForm({
        oracle_username: profile?.oracle_username || "",
        oracle_base_url: profile?.oracle_base_url || "",
        procurement_bu: profile?.procurement_bu || "",
        business_unit: profile?.business_unit || "",
        legal_employer: profile?.legal_employer || "",
        ledger_name: profile?.ledger_name || "",
        default_currency: profile?.default_currency || "USD",
        default_country: profile?.default_country || "US",
      });
      setPwd("");
      setErr("");
      setTestResult(null);
    }
  }, [setupOpen, profile]);

  if (!setupOpen) return null;

  const close = () => { setSetupOpen(false); setPendingAgent(null); };

  const save = async () => {
    if (!form.oracle_username) { setErr("Oracle username is required."); return; }
    if (!profile?.has_password && !pwd) { setErr("Oracle password is required for first-time setup."); return; }
    setSaving(true); setErr("");
    try {
      await api.post("/erp-agents/my-profile", { ...form, oracle_password: pwd });
      await onProfileSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/erp-agents/test-connection", {
        oracle_base_url: form.oracle_base_url || "",
        oracle_username: form.oracle_username || "",
        oracle_password: pwd || "",
      });
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || e.message, http_status: 0 });
    } finally { setTesting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={close}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white text-gray-900 max-w-xl w-full max-h-[90vh] border border-gray-300 rounded-xl shadow-xl overflow-hidden flex flex-col" data-testid="oracle-profile-modal">
        <div className="overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 flex items-center gap-1.5">
              <ShieldCheck size={14} weight="bold" className="text-[#0020C2]" /> My Oracle Fusion Profile
            </div>
            <h3 className="text-2xl font-black tracking-tight mt-1">Connect your Oracle identity</h3>
            <p className="text-xs text-gray-500 mt-1">Your credentials are encrypted at rest. These defaults are injected into every ERP agent command you run.</p>
          </div>
          <button onClick={close} data-testid="oracle-profile-close" className="rounded-md p-1 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">
              Oracle Pod URL <span className="text-gray-400 normal-case font-normal">(leave blank to use the server default)</span>
            </label>
            <div className="flex gap-2">
              <input value={form.oracle_base_url || ""} onChange={(e) => { setForm({ ...form, oracle_base_url: e.target.value }); setTestResult(null); }}
                data-testid="oracle-base-url" placeholder="https://ehxx-test.fa.em3.oraclecloud.com"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
              <button onClick={testConnection} disabled={testing} type="button" data-testid="oracle-test-connection"
                className="shrink-0 border border-[#0020C2] text-[#0020C2] rounded-md px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#0020C2] hover:text-white disabled:opacity-50 inline-flex items-center gap-1.5">
                {testing ? <Spinner size={12} className="animate-spin" /> : null}
                {testing ? "Testing…" : "Test"}
              </button>
            </div>
            {profile?.effective_base_url && (
              <div className="text-[11px] text-gray-500 mt-1 font-mono" data-testid="oracle-effective-base-url">
                Currently using: <span className="font-bold text-gray-800">{profile.effective_base_url}</span>
              </div>
            )}
            {testResult && (
              <div data-testid="oracle-test-result"
                className={`mt-2 flex items-start gap-2 border rounded-md px-3 py-2 text-xs ${
                  testResult.ok
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                    : "border-red-300 bg-red-50 text-[#DC2626]"
                }`}>
                {testResult.ok
                  ? <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
                  : <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold">
                    {testResult.ok ? "Connection OK" : "Connection failed"}
                    {testResult.http_status ? <span className="font-mono font-normal ml-1.5 opacity-80">· HTTP {testResult.http_status}</span> : null}
                    {typeof testResult.latency_ms === "number" && testResult.latency_ms > 0
                      ? <span className="font-mono font-normal ml-1.5 opacity-80">· {testResult.latency_ms} ms</span> : null}
                  </div>
                  <div className="mt-0.5 opacity-90 break-words">{testResult.message}</div>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Oracle Username</label>
              <input value={form.oracle_username || ""} onChange={(e) => setForm({ ...form, oracle_username: e.target.value })}
                data-testid="oracle-username" placeholder="e.g. FIN Support.Consultant"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">
                Oracle Password {profile?.has_password && <span className="text-gray-400 normal-case font-normal">(leave blank to keep)</span>}
              </label>
              <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                data-testid="oracle-password" placeholder={profile?.has_password ? "••••••••" : "Enter password"}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k} className={k === "ledger_name" ? "col-span-2" : ""}>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`oracle-prof-${k}`} placeholder={ph}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
              </div>
            ))}
          </div>
          {err && <div className="text-xs text-[#DC2626] border border-[#DC2626] rounded-md bg-red-50 px-3 py-2" data-testid="oracle-profile-error">{err}</div>}
          <button onClick={save} disabled={saving} data-testid="oracle-profile-save"
            className="bg-[#0020C2] text-white rounded-md px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
            {saving ? "Saving…" : "Save my profile"}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}